/**
 * KoneChat
 *
 * A ChatGPT-style assistant inside iCOP. It talks ONLY to KONE's internally
 * hosted model server, proxied by the backend (/api/konechat/*). Requests flow
 * through the shared axios instance so the MSAL bearer token is attached
 * automatically (see setupAxiosAuth.js).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import ChatMarkdown from './ChatMarkdown';
import './KoneChat.css';

const SYSTEM_PROMPT = {
  role: 'system',
  content:
    'You are KoneChat, an internal AI assistant for KONE employees running on ' +
    "KONE's own model server. Be helpful, concise, and professional.",
};

// Async flow: the backend returns a jobId immediately, then we poll for the
// result. The model runs on slow hardware, so allow several minutes.
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 6 * 60 * 1000; // give up after ~6 minutes

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Persist the conversation across route changes (survives leaving/returning to
// the KoneChat page within the session; cleared when the browser tab closes).
const STORE_KEY = 'icop.konechat.v1';

const loadStoredMessages = () => {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const saveStoredMessages = (messages) => {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(messages || []));
  } catch (e) {
    // Storage can be full/blocked; persistence is best-effort.
  }
};

const KoneChat = () => {
  // Conversation shown in the UI (excludes the system prompt). Restored from
  // sessionStorage so it survives leaving and returning to the KoneChat page.
  const [messages, setMessages] = useState(loadStoredMessages);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [config, setConfig] = useState({ enabled: true, model: '', loading: true });
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  // Guards against setting state after the component unmounts mid-poll.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Persist the conversation whenever it changes.
  useEffect(() => { saveStoredMessages(messages); }, [messages]);

  // Check whether the feature is configured (model server URL set).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(buildApiUrl(API_ENDPOINTS.koneChatConfig));
        if (!cancelled) {
          setConfig({ enabled: !!res.data?.enabled, model: res.data?.model || '', loading: false });
        }
      } catch (e) {
        if (!cancelled) setConfig({ enabled: false, model: '', loading: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-scroll to the newest message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;

    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setError('');
    setSending(true);

    try {
      // 1. Submit the prompt; the backend returns a jobId right away.
      const submit = await axios.post(buildApiUrl(API_ENDPOINTS.koneChatChat), {
        messages: [SYSTEM_PROMPT, ...nextMessages],
      });
      const jobId = submit.data?.jobId;
      if (!jobId) {
        setError('KoneChat did not start the request. Please try again.');
        return;
      }

      // 2. Poll for the result until done/error or we hit the overall timeout.
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        if (!mountedRef.current) return;

        let res;
        try {
          res = await axios.get(buildApiUrl(API_ENDPOINTS.koneChatResult(jobId)));
        } catch (e) {
          // The job row can disappear (TTL) or a transient network blip occurs.
          if (e?.response?.status === 404) {
            if (mountedRef.current) {
              setError('KoneChat lost track of this request. Please try again.');
            }
            return;
          }
          continue; // transient - keep polling
        }

        const status = res.data?.status;
        if (status === 'done') {
          const reply = res.data?.reply;
          if (mountedRef.current) {
            if (reply) {
              setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
            } else {
              setError('KoneChat returned an empty response. Please try again.');
            }
          }
          return;
        }
        if (status === 'error') {
          if (mountedRef.current) {
            setError(res.data?.error || 'KoneChat could not complete the request.');
          }
          return;
        }
        // status === 'pending' -> keep polling
      }
      if (mountedRef.current) {
        setError('KoneChat is taking longer than expected. Please try again.');
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        'Could not reach KoneChat. Please try again.';
      if (mountedRef.current) setError(msg);
    } finally {
      if (mountedRef.current) setSending(false);
    }
  }, [input, sending, messages]);

  const handleKeyDown = (e) => {
    // Enter to send, Shift+Enter for a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError('');
    setInput('');
    if (textareaRef.current) textareaRef.current.focus();
  };

  return (
    <div className="konechat">
      <div className="konechat-header">
        <div className="konechat-title">
          <span className="konechat-logo" aria-hidden="true">💬</span>
          <div>
            <h2>KoneChat</h2>
            <p className="konechat-subtitle">
              KONE internal AI assistant{config.model ? ` · ${config.model}` : ''}
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <button type="button" className="konechat-clear" onClick={clearChat} disabled={sending}>
            🧹 New chat
          </button>
        )}
      </div>

      {!config.loading && !config.enabled && (
        <div className="konechat-banner konechat-banner--warn">
          KoneChat is not configured yet. The internal model server needs to be set by the
          iCOP platform team before you can chat.
        </div>
      )}

      <div className="konechat-messages" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="konechat-empty">
            <div className="konechat-empty-icon">💬</div>
            <h3>Ask KoneChat anything</h3>
            <p>Your conversation runs on KONE's own model server. Nothing is sent to public AI services.</p>
          </div>
        )}
        {messages.map((m, idx) => (
          <div key={idx} className={`konechat-msg konechat-msg--${m.role}`}>
            <div className="konechat-avatar" aria-hidden="true">
              {m.role === 'user' ? '🧑' : '🤖'}
            </div>
            <div className="konechat-bubble">
              {/* Assistant replies are Markdown (tables/headings/bold/code);
                  render them properly. User messages stay plain text. */}
              {m.role === 'assistant'
                ? <ChatMarkdown text={m.content} />
                : m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="konechat-msg konechat-msg--assistant">
            <div className="konechat-avatar" aria-hidden="true">🤖</div>
            <div className="konechat-bubble konechat-typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        )}
      </div>

      {error && <div className="konechat-banner konechat-banner--error">{error}</div>}

      <div className="konechat-input-row">
        <textarea
          ref={textareaRef}
          className="konechat-input"
          placeholder="Message KoneChat…  (Enter to send, Shift+Enter for a new line)"
          value={input}
          disabled={sending || (!config.loading && !config.enabled)}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <button
          type="button"
          className="konechat-send"
          onClick={sendMessage}
          disabled={sending || !input.trim() || (!config.loading && !config.enabled)}
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default KoneChat;
